import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useBoolean } from 'usehooks-ts';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import {
  Badge,
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Flex,
  Icon,
  toast,
} from '@kognitos/lattice';
import type {
  V2BookConnectionDescriptor,
  Bdkv2BookDescriptor,
  Bdkv2Value,
} from '@/generated/hooks/bdk/models';
import {
  getRetrieveAllWorkspaceBookConnectionsQueryKey,
  useRetrieveAllWorkspaceBookConnections,
} from '@/generated/hooks/bdk/v2/services/library';
import {
  getExtractAutomationCodeConnectionsQueryKey,
  useSwitchAutomationConnections,
  useAddAutomationConnections,
  useForkPublishedAutomation,
} from '@/generated/hooks/grimoire/v1/api';
import type { V1CredentialInfo } from '@/generated/hooks/grimoire/models';
import {
  AddConnectionDialog,
  ConnectionStatusTag,
  EditConnectionDialog,
  SelectConnectionDialog,
  isConnectionReady,
  useConnectionActions,
  useOAuthCallbackError,
  type IFilteredConnection,
} from '@/modules/integrations';
import { isSharedConnection } from '@/modules/integrations/utils';
import { captureException } from '@/modules/sentry';
import { errorHandler } from '@/shared/utils/error-handler';
import {
  useWaitForConnectionToBeReady,
  type IWaitForConnectionResult,
} from '@/modules/integrations/hooks/useWaitForConnectionToBeReady';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores/user-preferences.store';
import { usePermissions } from '@/shared/permissions';
import { TooltipDisabledButton } from '@/shared/components';
import { getRoutePath } from '@/routes/util';
import {
  FilterBuilder,
  showToast,
  validateOAuthRedirectUrl,
} from '@/shared/utils';
import { hasPendingConnectionUpgrade } from '@/modules/automation/hooks/use-upgrade-automation-connection';
import { hasPendingTriggerConnection } from '@/modules/triggers/utils/trigger-connection-intent';
import {
  hasManagedOAuthReturn,
  useManagedOAuthReturn,
  type IOAuthConnectionResume,
} from '@/modules/credentials';

/** Tags the menu's sign-ins, so a chat card on the same page doesn't reopen them. */
export const AUTOMATION_MENU_OAUTH_ORIGIN = 'automation-menu';

/**
 * Builds a user-facing toast message for a connection that did not become
 * ready. Uses the backend-provided reason when available, otherwise falls back
 * to a message describing the specific terminal state.
 *
 * @param result - Connection wait result
 * @param tAutomation - Translation function for the automation namespace
 * @returns Message to show the user
 */
const getConnectionNotReadyMessage = (
  result: IWaitForConnectionResult,
  tAutomation: TFunction<'automation'>,
): string => {
  const backendReason = result.connection?.status?.reason?.trim();

  if (result.isFailed) {
    return (
      backendReason || tAutomation('automation.integrations.connectionFailed')
    );
  }
  if (result.isConditionsNotMet) {
    return (
      backendReason ||
      tAutomation('automation.integrations.connectionConditionsNotMet')
    );
  }
  if (result.isTimedOut) {
    return tAutomation('automation.integrations.connectionTimedOut');
  }
  return (
    backendReason || tAutomation('automation.integrations.connectionNotReady')
  );
};

interface IAutomationConnectionMenuProps {
  // This connectionId should always be the test connection id
  // NEVER pass the production connection id
  testConnectionId?: string;
  connectionAlias: string;
  automationId: string;
  isProdAvailable?: boolean;
  isTestAvailable?: boolean;
  integration?: Bdkv2BookDescriptor;
  /** Whether the book descriptor failed to load, as opposed to still loading. */
  isIntegrationUnavailable?: boolean;
  isDraft?: boolean;
  readonly?: boolean;
  /** When true, shows badge only without configure button or menu (for trigger connections) */
  isTrigger?: boolean;
}

const EMPTY_CONNECTIONS: V2BookConnectionDescriptor[] = [];

function AutomationConnectionMenu({
  testConnectionId,
  connectionAlias,
  automationId,
  isProdAvailable,
  isTestAvailable,
  integration,
  isIntegrationUnavailable = false,
  isDraft = true,
  readonly = false,
  isTrigger = false,
}: IAutomationConnectionMenuProps) {
  const { t: tAutomation } = useTranslation('automation');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    value: isAddConnectionDialogOpen,
    setTrue: openAddConnectionDialog,
    setFalse: closeAddConnectionDialog,
  } = useBoolean();
  const {
    value: isAddProductionCredentialsDialogOpen,
    setTrue: openAddProductionCredentialsDialog,
    setFalse: closeAddProductionCredentialsDialog,
  } = useBoolean();
  const {
    value: isSelectConnectionDialogOpen,
    setTrue: openSelectConnectionDialog,
    setFalse: closeSelectConnectionDialog,
  } = useBoolean();
  const {
    value: isCheckingConnection,
    setTrue: startCheckingConnection,
    setFalse: stopCheckingConnection,
  } = useBoolean();

  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  // Back from an OAuth provider: reopen the wizard where it left off.
  const [oauthResume, setOAuthResume] = useState<IOAuthConnectionResume | null>(
    null,
  );
  const handleOAuthResume = useCallback(
    (resume: IOAuthConnectionResume) => {
      setOAuthResume(resume);
      openAddConnectionDialog();
    },
    [openAddConnectionDialog],
  );
  useManagedOAuthReturn({
    integrationName: integration?.name ?? '',
    origin: AUTOMATION_MENU_OAUTH_ORIGIN,
    onResume: handleOAuthResume,
  });

  const { hasPermission } = usePermissions();
  const canManageAutomationConnections = hasPermission(
    ['AddAutomationConnections'],
    { organization: organizationId, workspace: workspaceId },
  );
  const canSwitchAutomationConnections = hasPermission(
    ['SwitchAutomationConnections'],
    { organization: organizationId, workspace: workspaceId },
  );

  const waitForConnectionToBeReady = useWaitForConnectionToBeReady();

  const {
    createConnection,
    updateConnection,
    createOAuthAuthorizationConnection,
  } = useConnectionActions(organizationId, workspaceId);

  const { data: connectionsData } = useRetrieveAllWorkspaceBookConnections(
    organizationId,
    workspaceId,
    {
      filter: FilterBuilder.create()
        .equals('book_name', integration?.name || '')
        .build(),
    },
    { query: { enabled: Boolean(integration?.name) } },
  );

  const {
    mutate: forkPublishedAutomation,
    isPending: isForkingPublishedAutomation,
  } = useForkPublishedAutomation();

  const {
    mutate: switchAutomationConnections,
    isPending: isSwitchingAutomationConnections,
  } = useSwitchAutomationConnections({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getExtractAutomationCodeConnectionsQueryKey(
            organizationId,
            workspaceId,
            automationId,
          ),
        });
      },
      onError: error => {
        errorHandler(error, () => {
          toast.error(
            tAutomation('automation.integrations.switchConnectionError'),
          );
        });
      },
    },
  });

  const {
    mutate: addAutomationConnections,
    isPending: isAddingAutomationConnections,
  } = useAddAutomationConnections({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getExtractAutomationCodeConnectionsQueryKey(
            organizationId,
            workspaceId,
            automationId,
          ),
        });
      },
      onError: error => {
        errorHandler(error, () => {
          toast.error(
            tAutomation('automation.integrations.addConnectionError'),
          );
        });
      },
    },
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const oauthConnectionIdRef = useRef<string | null>(null);
  const showOAuthCallbackError = useOAuthCallbackError();

  /**
   * Handles OAuth sign-in click by creating an OAuth authorization connection
   * and redirecting to the OAuth provider.
   */
  const handleOAuthClick = useCallback(
    async (
      authMethodId: string,
      connectionName: string,
      credentials: Record<string, string>,
      labels: string[],
      configMap?: Record<string, Bdkv2Value>,
    ) => {
      try {
        const authorizeUrl = await createOAuthAuthorizationConnection(
          integration || {},
          authMethodId,
          connectionName,
          credentials,
          labels,
          configMap,
        );

        validateOAuthRedirectUrl(authorizeUrl);
        window.location.assign(authorizeUrl);
      } catch (error) {
        captureException(
          error instanceof Error ? error : new Error(String(error)),
          {
            tags: {
              component: 'AutomationConnectionMenu',
              action: 'createOAuthAuthorizationConnection',
            },
          },
        );
        throw error;
      }
    },
    [integration, createOAuthAuthorizationConnection],
  );

  // The full connection object for the selected connection
  const connection = connectionsData?.data?.book_connections?.find(
    conn =>
      conn.book_name === integration?.name &&
      conn.book_version === integration?.version &&
      conn.connection_id === testConnectionId,
  );

  // All ready connections of the integration (inactive/failed are not selectable)
  const integrationConnections = (
    connectionsData?.data?.book_connections || EMPTY_CONNECTIONS
  ).filter(isConnectionReady);

  const addConnection = async (connection: V1CredentialInfo) => {
    if (integration?.name && integration?.version && connection.connection_id) {
      startCheckingConnection();
      const result = await waitForConnectionToBeReady(connection.connection_id);
      if (result.isReady) {
        addAutomationConnections(
          {
            organization: organizationId,
            workspace: workspaceId,
            automation: automationId,
            data: {
              connections: [connection],
            },
          },
          {
            onSuccess: () => {
              toast.success(
                tAutomation('automation.integrations.addConnectionSuccess'),
              );
            },
          },
        );
      } else {
        toast.error(getConnectionNotReadyMessage(result, tAutomation));
      }
      stopCheckingConnection();
    }
  };

  const switchConnection = async (
    oldConnectionId: string,
    newConnectionId: string,
    onSuccess?: () => void,
  ) => {
    if (integration?.name && integration?.version) {
      startCheckingConnection();
      const result = await waitForConnectionToBeReady(newConnectionId);
      if (result.isReady) {
        switchAutomationConnections(
          {
            organization: organizationId,
            workspace: workspaceId,
            automation: automationId,
            data: {
              switches: [
                {
                  old_connection_id: oldConnectionId,
                  new_connection_id: newConnectionId,
                  connection_alias: connectionAlias,
                },
              ],
            },
          },
          { onSuccess },
        );
      } else {
        toast.error(getConnectionNotReadyMessage(result, tAutomation));
      }
      stopCheckingConnection();
    }
  };

  /**
   * Handles connection action (switch or add) for a given connection.
   */
  const handleConnectionActionForDraft = (newConnectionId: string) => {
    const shouldSwitch = isTestAvailable && !!testConnectionId;

    if (shouldSwitch) {
      switchConnection(testConnectionId, newConnectionId, () => {
        toast.success(
          tAutomation('automation.integrations.switchConnectionSuccess'),
        );
      });
    } else {
      addConnection({
        book: integration?.name || '',
        book_version: integration?.version || '',
        connection_id: newConnectionId,
        connection_alias: connectionAlias,
      });
    }
  };

  /**
   * Handles connection action for published automations
   * (forks first to create a draft, then handles connection switch).
   */
  const handleConnectionSwitchForPublished = (newConnectionId: string) => {
    forkPublishedAutomation(
      {
        organization: organizationId,
        workspace: workspaceId,
        automation: automationId,
        data: {},
      },
      {
        onSuccess: () => {
          const shouldSwitch = isTestAvailable && testConnectionId;

          if (shouldSwitch) {
            switchConnection(testConnectionId, newConnectionId, () => {
              showToast(
                tAutomation(
                  'automation.integrations.switchConnectionSuccessForPublishedAutomationTitle',
                ),
                tAutomation(
                  'automation.integrations.switchConnectionSuccessForPublishedAutomation',
                ),
                'success',
              );
              navigate(
                getRoutePath('DRAFT_EDITOR', {
                  orgId: organizationId,
                  workspaceId: workspaceId,
                  // Automation ID won't change for forked draft
                  // So we use the automation ID
                  draftId: automationId,
                }),
              );
            });
          }
        },
        onError: error => {
          captureException(error, {
            tags: { module: 'automation', action: 'forkPublishedAutomation' },
          });
          toast.error(
            tAutomation('automation.integrations.switchConnectionError'),
          );
        },
      },
    );
  };

  // eslint-disable-next-line no-restricted-syntax -- reason: Handle OAuth callback URL param when component mounts after redirect
  useEffect(() => {
    const oauthConnectionId = searchParams.get('data');
    const connectionError = searchParams.get('error');

    // A trigger dialog on this page owns this OAuth return. Linking the
    // connection here would attach it to the automation and fork it if
    // published.
    if (hasPendingTriggerConnection(pathname)) {
      return;
    }
    // A managed sign-in's return: ?data= is a credential's id, not a connection's.
    if (hasManagedOAuthReturn(searchParams)) {
      return;
    }

    if (connectionError) {
      showOAuthCallbackError(connectionError, 'AutomationConnectionMenu');
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('error');
      setSearchParams(nextParams, { replace: true });
    }

    if (
      !oauthConnectionId ||
      oauthConnectionIdRef.current === oauthConnectionId
    ) {
      return;
    }

    // A pending connection upgrade owns this OAuth redirect. The upgrade hook's
    // useEffect (in AutomationIntegrationsPanel) handles resume and URL cleanup,
    // so the menu must not also attempt to switch/add the connection.
    if (hasPendingConnectionUpgrade(automationId)) {
      oauthConnectionIdRef.current = oauthConnectionId;
      return;
    }

    oauthConnectionIdRef.current = oauthConnectionId;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('data');
    nextParams.delete('error');
    setSearchParams(nextParams, { replace: true });

    const linkOAuthConnection = async () => {
      const result = await waitForConnectionToBeReady(oauthConnectionId);
      if (!result.isReady) return;

      if (
        result.connection?.book_name !== integration?.name ||
        result.connection?.book_version !== integration?.version
      ) {
        return;
      }

      if (isDraft) {
        handleConnectionActionForDraft(oauthConnectionId);
      } else {
        handleConnectionSwitchForPublished(oauthConnectionId);
      }
    };

    linkOAuthConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- searchParams triggers re-run, others are stable
  }, [searchParams, setSearchParams, showOAuthCallbackError, pathname]);

  const handleCreateConnection = (
    draftConnection?: V2BookConnectionDescriptor,
  ) => {
    if (!draftConnection?.connection_id) {
      return;
    }

    closeAddConnectionDialog();

    if (isDraft) {
      handleConnectionActionForDraft(draftConnection.connection_id);
    } else {
      handleConnectionSwitchForPublished(draftConnection.connection_id);
    }
  };

  const handleSelectConnection = (selectedConnection: IFilteredConnection) => {
    closeSelectConnectionDialog();

    if (isDraft) {
      handleConnectionActionForDraft(selectedConnection.connection_id);
    } else {
      handleConnectionSwitchForPublished(selectedConnection.connection_id);
    }
  };

  const handleCreateProductionCredentials = () => {
    closeAddProductionCredentialsDialog();
    closeSelectConnectionDialog();
    queryClient.invalidateQueries({
      queryKey: getRetrieveAllWorkspaceBookConnectionsQueryKey(
        organizationId,
        workspaceId,
      ),
    });
  };

  const handleAddConnectionFromSelectDialog = () => {
    closeSelectConnectionDialog();
    openAddConnectionDialog();
  };

  const handleAddMissingConnection = () => {
    // If there are existing connections, show the select dialog
    // Otherwise, show the add dialog
    const hasConnections = Boolean(integrationConnections.length);
    if (hasConnections) {
      openSelectConnectionDialog();
    } else {
      openAddConnectionDialog();
    }
  };

  const disableActions =
    isCheckingConnection ||
    isSwitchingAutomationConnections ||
    isAddingAutomationConnections ||
    isForkingPublishedAutomation;

  // Keep in sync with the dialogs below, which only render with a descriptor.
  const canOpenConnectionDialogs = Boolean(integration);

  const getBadge = () => {
    if (!isTestAvailable) {
      // For triggers, show a non-interactive badge instead of configure button
      if (isTrigger) {
        return (
          <Badge variant="outline" className="bg-accent">
            <Flex gap={1} align="center">
              <Icon type="AlertCircle" size="xs" className="text-warning" />
              {tAutomation('automation.integrations.prodMissing')}
            </Flex>
          </Badge>
        );
      }
      if (!canManageAutomationConnections) {
        return (
          <TooltipDisabledButton
            tooltip={tAutomation(
              'automation.integrations.connectionActionsDisabled',
            )}
            variant="outline"
            size="sm"
          >
            {tAutomation('automation.integrations.configureConnection')}
          </TooltipDisabledButton>
        );
      }
      if (!canOpenConnectionDialogs) {
        // A descriptor that is merely absent may still be on its way, or its
        // query may be disabled — neither is a failure worth explaining away.
        return isIntegrationUnavailable ? (
          <TooltipDisabledButton
            tooltip={tAutomation(
              'automation.integrations.connectionActionsUnavailable',
            )}
            variant="outline"
            size="sm"
          >
            {tAutomation('automation.integrations.configureConnection')}
          </TooltipDisabledButton>
        ) : (
          <Button size="sm" variant="outline" isLoading disabled>
            {tAutomation('automation.integrations.configureConnection')}
          </Button>
        );
      }
      return (
        <Button
          size="sm"
          variant="outline"
          onClick={handleAddMissingConnection}
          isLoading={disableActions}
          disabled={disableActions}
        >
          {tAutomation('automation.integrations.configureConnection')}
        </Button>
      );
    }
    if (isProdAvailable) {
      // Credentials exist on the automation, but the live connection record may
      // be inactive/failed. Only show Connected when the record is READY.
      if (connection && !isConnectionReady(connection)) {
        return (
          <ConnectionStatusTag
            connections={[connection]}
            status={connection.status?.state}
          />
        );
      }
      return (
        <Badge variant="outline" className="bg-accent">
          <Flex gap={1} align="center">
            <Icon type="CircleCheck" size="xs" className="text-success" />
            {tAutomation('automation.integrations.connected')}
          </Flex>
        </Badge>
      );
    }
    if (!isProdAvailable) {
      return (
        <Badge variant="outline" className="bg-accent">
          <Flex gap={1} align="center">
            <Icon type="AlertCircle" size="xs" className="text-warning" />
            {tAutomation('automation.integrations.prodMissing')}
          </Flex>
        </Badge>
      );
    }
    return null;
  };

  const getDropdownMenu = () => {
    if (!isTestAvailable) {
      return null;
    }

    const menuItems = [
      <DropdownMenuItem
        key="switch"
        onClick={openSelectConnectionDialog}
        disabled={!canSwitchAutomationConnections || !canOpenConnectionDialogs}
      >
        <Icon type="SwitchCamera" size="sm" />
        {tAutomation('automation.integrations.switch')}
      </DropdownMenuItem>,
    ];

    // Adding production credentials edits the connection, which BDK refuses on
    // a shared one. Switching to a different connection stays available.
    if (!isProdAvailable && !isSharedConnection(connection)) {
      menuItems.push(
        <DropdownMenuItem
          key="addProductionCredentials"
          onClick={openAddProductionCredentialsDialog}
          disabled={!canOpenConnectionDialogs}
        >
          <Icon type="Plus" size="sm" />
          {tAutomation('automation.integrations.addProdConnection')}
        </DropdownMenuItem>,
      );
    }

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            className="h-6 w-6"
            isLoading={disableActions}
            disabled={disableActions}
          >
            <Icon type="MoreVertical" size="sm" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">{menuItems}</DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <>
      <Flex
        gap={1}
        align="center"
        className={cn({ 'mr-2': readonly || isTrigger })}
      >
        {getBadge()}
        {!readonly && !isTrigger && getDropdownMenu()}
      </Flex>
      {integration && (
        <>
          <EditConnectionDialog
            isOpen={isAddProductionCredentialsDialogOpen}
            onCancel={closeAddProductionCredentialsDialog}
            onSuccess={handleCreateProductionCredentials}
            createConnection={createConnection}
            updateConnection={updateConnection}
            connections={connection ? [connection] : []}
            integration={integration}
            publishOnlyMode
          />
          <AddConnectionDialog
            key={oauthResume ? 'oauth-resume' : 'add'}
            isOpen={isAddConnectionDialogOpen}
            integration={integration}
            onCancel={() => {
              setOAuthResume(null);
              closeAddConnectionDialog();
            }}
            onSuccess={draftConnection => {
              setOAuthResume(null);
              handleCreateConnection(draftConnection);
            }}
            onOAuthClick={handleOAuthClick}
            createConnection={createConnection}
            updateConnection={updateConnection}
            supportsOAuthResume
            oauthOrigin={AUTOMATION_MENU_OAUTH_ORIGIN}
            oauthResume={oauthResume}
          />
          <SelectConnectionDialog
            isOpen={isSelectConnectionDialogOpen}
            onClose={closeSelectConnectionDialog}
            integration={integration}
            connections={integrationConnections}
            onSelectConnection={handleSelectConnection}
            onAddConnection={handleAddConnectionFromSelectDialog}
            selectedConnection={testConnectionId}
          />
        </>
      )}
    </>
  );
}

export default AutomationConnectionMenu;
